import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { GlobeMethods } from "react-globe.gl";
import earthDayImage from "@/assets/images/earth/earth-day.webp";
import earthTopologyImage from "@/assets/images/earth/earth-topology.png";
import nightSkyImage from "@/assets/images/earth/night-sky.png";

const globePromise = import("react-globe.gl");
const Globe = lazy(() => globePromise);

// Pre-computed rgba strings (0–100 in 1% steps)
const COLOR_CACHE: string[] = [];
for (let i = 0; i <= 100; i++) {
	COLOR_CACHE[i] = `rgba(255,255,255,${(i / 100).toFixed(2)})`;
}
function arcColor(opacity: number): string {
	return COLOR_CACHE[Math.round(Math.max(0, Math.min(1, opacity)) * 100)];
}

// --- Types -----------------------------------------------------------

type SceneChild = { isLight?: boolean };
type SceneLike = { children: SceneChild[]; remove: (o: SceneChild) => void; add: (o: unknown) => void };
type GlobeRenderer = { toneMapping?: number; toneMappingExposure?: number; outputColorSpace?: number };
type GlobeControls = { autoRotate: boolean; autoRotateSpeed: number; enableZoom: boolean; enablePan: boolean; enableRotate: boolean; update: () => void };
type DirLight = { position: { set: (x: number, y: number, z: number) => void } };
type ThreeLike = { DirectionalLight: new (c: number, i: number) => DirLight; AmbientLight: new (c: number, i: number) => unknown; ACESFilmicToneMapping: number; SRGBColorSpace: number };

// --- Cities (30 — good global spread) --------------------------------

const CITIES: [number, number][] = [
	// North America
	[40.71, -74.01], [34.05, -118.24], [37.77, -122.42], [41.88, -87.63],
	[43.65, -79.38], [47.61, -122.33], [30.27, -97.74], [25.76, -80.19],
	// Latin America
	[19.43, -99.13], [-23.55, -46.63], [-34.60, -58.38], [4.71, -74.07],
	// Europe
	[51.51, -0.13], [48.86, 2.35], [52.52, 13.41], [40.42, -3.70],
	// Asia
	[35.68, 139.65], [1.35, 103.82], [37.57, 126.98], [22.32, 114.17],
	[19.08, 72.88], [12.97, 77.59], [25.20, 55.27], [13.76, 100.50],
	// Oceania
	[-33.87, 151.21], [-36.85, 174.76],
	// Africa
	[30.04, 31.24], [-33.92, 18.42], [6.52, 3.38], [-1.29, 36.82],
];

// --- Arc data --------------------------------------------------------

interface Arc {
	startLat: number; startLng: number;
	endLat: number; endLng: number;
	arcAlt: number;
	baseOpacity: number;
	opacity: number;
}

function generateArcs(): Arc[] {
	const arcs: Arc[] = [];
	const n = CITIES.length;
	const used = new Set<string>();

	const add = (i: number, j: number) => {
		const key = i < j ? `${i}-${j}` : `${j}-${i}`;
		if (used.has(key)) return;
		used.add(key);
		const [lat1, lng1] = CITIES[i];
		const [lat2, lng2] = CITIES[j];
		const dist = Math.sqrt((lat1 - lat2) ** 2 + (lng1 - lng2) ** 2);
		const alt = 0.04 + Math.random() * 0.05 + Math.min(dist / 300, 1) * 0.14 + Math.random() * 0.07;
		const baseOpacity = 0.01 + Math.random() * 0.03;
		arcs.push({ startLat: lat1, startLng: lng1, endLat: lat2, endLng: lng2, arcAlt: alt, baseOpacity, opacity: baseOpacity });
	};

	// 2 random connections per city → ~60 arcs total
	for (let i = 0; i < n; i++) {
		for (let c = 0; c < 2; c++) {
			let j = Math.floor(Math.random() * n);
			if (j === i) j = (j + 1) % n;
			add(i, j);
		}
	}
	return arcs;
}

// --- Pulse animation -------------------------------------------------

const PULSE_UP = 200;   // ms
const PULSE_DOWN = 4000; // ms
const easeOut = (t: number) => 1 - (1 - t) ** 4;
const easeExp = (t: number) => t === 1 ? 1 : 1 - 2 ** (-10 * t);

// --- Sun position ----------------------------------------------------

function getSunPosition() {
	const now = new Date();
	const lng = (12 - now.getUTCHours() - now.getUTCMinutes() / 60) * 15;
	const rad = (lng * Math.PI) / 180;
	return { x: 200 * Math.sin(rad), y: 30, z: 200 * Math.cos(rad) };
}

// --- Component -------------------------------------------------------

interface EarthProps {
	width?: number;
	height?: number;
	enablePointerInteraction?: boolean;
	atmosphereAltitude?: number;
	noBackground?: boolean;
	globeOffset?: [number, number];
	initialLat?: number;
	initialLng?: number;
	altitude?: number;
	autoRotateSpeed?: number;
	isPaused?: boolean;
}

export default function Earth({
	width,
	height,
	enablePointerInteraction = true,
	atmosphereAltitude = 0.08,
	noBackground = true,
	globeOffset = [0, 0],
	initialLat = 39,
	initialLng = -140,
	altitude = 1.8,
	autoRotateSpeed = -0.21,
	isPaused = false,
}: EarthProps) {
	const globeEl = useRef<GlobeMethods | undefined>(undefined);
	const animationFrameRef = useRef<number | null>(null);
	const rafRef = useRef<number>(0);
	const [mounted, setMounted] = useState(false);
	const [isClient, setIsClient] = useState(false);

	// Arc data lives in a ref; React state only holds a snapshot for rendering
	const arcsRef = useRef<Arc[]>([]);
	const [arcsSnapshot, setArcsSnapshot] = useState<Arc[]>([]);

	// Pulse bookkeeping: Map<arcIndex, { start, phase }>
	const pulsing = useRef<Map<number, { start: number; phase: "up" | "down" }>>(new Map());

	// Init arcs once
	useEffect(() => {
		if (!isClient) return;
		arcsRef.current = generateArcs();
		setArcsSnapshot(arcsRef.current.slice());
	}, [isClient]);

	useEffect(() => {
		setIsClient(true);
		setMounted(true);
	}, []);

	// Cleanup controls animation loop
	useEffect(() => {
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, []);

	// Pulse rAF loop
	useEffect(() => {
		if (!isClient || isPaused) return;

		let lastFlush = 0;
		const arcs = arcsRef.current;
		const n = arcs.length;

		// Schedule next pulse every 500–1500ms
		let nextPulse = performance.now() + 500;

		const tick = (now: number) => {
			// Trigger new pulse
			if (now >= nextPulse) {
				nextPulse = now + 500 + Math.random() * 1000;
				// Pick a random arc that isn't already pulsing
				let idx = Math.floor(Math.random() * n);
				for (let tries = 0; tries < 5 && pulsing.current.has(idx); tries++) {
					idx = Math.floor(Math.random() * n);
				}
				if (!pulsing.current.has(idx)) {
					pulsing.current.set(idx, { start: now, phase: "up" });
				}
			}

			// Animate active pulses
			let dirty = false;
			for (const [idx, state] of pulsing.current) {
				const arc = arcs[idx];
				const elapsed = now - state.start;

				if (state.phase === "up") {
					const p = Math.min(elapsed / PULSE_UP, 1);
					arc.opacity = arc.baseOpacity + (1 - arc.baseOpacity) * easeOut(p);
					if (p >= 1) pulsing.current.set(idx, { start: now, phase: "down" });
				} else {
					const p = Math.min(elapsed / PULSE_DOWN, 1);
					arc.opacity = 1 - (1 - arc.baseOpacity) * easeExp(p);
					if (p >= 1) { arc.opacity = arc.baseOpacity; pulsing.current.delete(idx); }
				}
				dirty = true;
			}

			// Flush to React at ~15fps
			if (dirty && now - lastFlush >= 66) {
				lastFlush = now;
				setArcsSnapshot(arcs.slice());
			}

			rafRef.current = requestAnimationFrame(tick);
		};

		rafRef.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafRef.current);
	}, [isClient, isPaused]);

	if (!isClient || !mounted) return <div className="relative h-full w-full" />;

	return (
		<div className={`relative h-full w-full ${enablePointerInteraction ? "cursor-grab active:cursor-grabbing" : ""}`}>
			<Suspense fallback={null}>
				<Globe
					ref={globeEl}
					globeOffset={globeOffset}
					width={width}
					height={height}
					backgroundColor="#0a0a0a"
					globeImageUrl={earthDayImage}
					bumpImageUrl={earthTopologyImage}
					backgroundImageUrl={noBackground ? undefined : nightSkyImage}
					atmosphereColor="#4a90c2"
					atmosphereAltitude={atmosphereAltitude}
					enablePointerInteraction={enablePointerInteraction}
					arcsData={arcsSnapshot}
					arcStartLat="startLat"
					arcStartLng="startLng"
					arcEndLat="endLat"
					arcEndLng="endLng"
					arcColor={(d: object) => arcColor((d as Arc).opacity)}
					arcAltitude={(d: object) => (d as Arc).arcAlt}
					arcStroke={0.35}
					arcsTransitionDuration={0}
					onGlobeReady={() => {
						if (!globeEl.current) return;
						const globe = globeEl.current;

						globe.pointOfView({ lat: initialLat, lng: initialLng, altitude });

						const controls = globe.controls?.() as GlobeControls | undefined;
						if (controls) {
							controls.autoRotate = true;
							controls.autoRotateSpeed = autoRotateSpeed;
							controls.enableZoom = false;
							controls.enablePan = false;
							controls.enableRotate = enablePointerInteraction;

							const animate = () => {
								controls.update();
								animationFrameRef.current = requestAnimationFrame(animate);
							};
							animate();
						}

						const scene = globe.scene?.() as SceneLike | undefined;
						const THREE = (window as Window & { THREE?: ThreeLike }).THREE;
						if (!scene || !THREE) return;

						scene.children.filter(c => c.isLight).forEach(l => scene.remove(l));

						const sun = getSunPosition();
						const sunLight = new THREE.DirectionalLight(0xffffff, 2.8);
						sunLight.position.set(sun.x, sun.y, sun.z);
						scene.add(sunLight);

						const fill = new THREE.DirectionalLight(0x223344, 0.08);
						fill.position.set(-sun.x, -sun.y, -sun.z);
						scene.add(fill);

						scene.add(new THREE.AmbientLight(0x112233, 0.15));

						const renderer = globe.renderer?.() as GlobeRenderer | undefined;
						if (renderer) {
							renderer.toneMapping = THREE.ACESFilmicToneMapping;
							renderer.toneMappingExposure = 1.1;
							renderer.outputColorSpace = THREE.SRGBColorSpace;
							// #383838 linear ≈ #0a0a0a sRGB (matches page bg)
							(renderer as GlobeRenderer & { setClearColor: (c: number, a: number) => void }).setClearColor(0x383838, 1);
						}
					}}
				/>
			</Suspense>
		</div>
	);
}
